//src/components/ActionSection.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SquareCheck as CheckSquare, ChevronDown } from 'lucide-react';
import { Button } from './Button';
import { supabase } from '../lib/supabase';
import { GoogleTasksSync } from './GoogleTasksSync';
import { cn } from '../utils/cn';
import { fetchGoalsWithLadders, getMostActiveLadderPath, updateStepCompletion, checkAndCompleteLadder } from '../lib/ladderUtils';
import type { LadderStep } from '../types/ladder';
import { Checkbox } from './Checkbox';

export default function ActionSection() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<any[]>([]);
  const [newTask, setNewTask] = useState('');
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState('');
  const [lastRungs, setLastRungs] = useState<Array<{ rung: LadderStep; ladderId: string; goalTitle: string }>>([]);
  const [updatingRung, setUpdatingRung] = useState<string | null>(null);
 
  useEffect(() => {
    async function fetchUserTimeZone() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('time_zone')
          .eq('id', session.user.id)
          .single();

        if (profile?.time_zone) {
          setUserTimeZone(profile.time_zone);
        } else {
          // Default to browser's timezone if not set in profile
          setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        }
      } catch (error) {
        console.error('Error fetching user timezone:', error);
        // Fall back to browser's timezone
        setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      }
    }

    fetchUserTimeZone();
  }, []);

  useEffect(() => {
    async function fetchTasks() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (taskError) {
        console.error('Error fetching tasks:', taskError);
        return;
      }

      setTasks(taskData || []);
    }

    fetchTasks();
  }, [userTimeZone]);

  useEffect(() => {
    async function fetchLastRungs() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const goals = await fetchGoalsWithLadders(session.user.id);
      if (goals.length === 0) return;

      const rungs: Array<{ rung: LadderStep; ladderId: string; goalTitle: string }> = [];

      for (const goal of goals) {
        const ladderPath = await getMostActiveLadderPath(goal.id);
        if (!ladderPath || ladderPath.ladderPath.length === 0) continue;

        const currentLadder = ladderPath.ladderPath[ladderPath.ladderPath.length - 1];
        const sortedSteps = [...currentLadder.steps].sort((a, b) => a.step_number - b.step_number);

        const firstIncompleteStep = sortedSteps.find(step => !step.is_completed);

        if (firstIncompleteStep) {
          rungs.push({
            rung: firstIncompleteStep,
            ladderId: currentLadder.id,
            goalTitle: goal.name || 'Untitled Goal'
          });
        }
      }

      setLastRungs(rungs);
    }

    fetchLastRungs();
  }, []);

  const handleToggleTask = useCallback(async (taskId: string, currentStatus: boolean) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const now = new Date().toISOString();

      await supabase
        .from('tasks')
        .update({
          completed: !currentStatus,
          completed_at: !currentStatus ? now : null
        })
        .eq('id', taskId);

      setTasks(prev =>
        prev.map(task =>
          task.id === taskId ? {
            ...task,
            completed: !currentStatus,
            completed_at: !currentStatus ? now : null
          } : task
        )
      );

      const today = new Date().toISOString().slice(0, 10);
      await supabase
        .from('growth_days')
        .upsert([
          {
            user_id: session.user.id,
            date: today,
            task_completed: true,
            updated_at: new Date().toISOString()
          }
        ], { onConflict: ['user_id', 'date'] });
    } catch (err: any) {
      console.error('Error updating task:', err.message);
      setError(err.message);
    }
  }, []);

  const handleAddTask = async () => {
    if (!newTask.trim()) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('tasks')
        .insert([
          {
            user_id: session.user.id,
            text: newTask.trim(),
            completed: false
          }
        ])
        .select();

      if (error) throw error;

      setTasks(prev => [data![0], ...prev]);
      setNewTask('');
    } catch (err: any) {
      console.error('Error adding task:', err.message);
      setError(err.message);
    }
  };

  const handleToggleRung = async (rungId: string, ladderId: string, currentCompleted: boolean) => {
    setUpdatingRung(rungId);
    try {
      const success = await updateStepCompletion(rungId, !currentCompleted);
      if (success) {
        await checkAndCompleteLadder(ladderId);
        setLastRungs(prev =>
          prev.map(item =>
            item.rung.id === rungId
              ? { ...item, rung: { ...item.rung, is_completed: !currentCompleted } }
              : item
          )
        );
      }
    } catch (err: any) {
      console.error('Error updating rung:', err);
      setError('Failed to update step');
    } finally {
      setUpdatingRung(null);
    }
  };

  const incompleteTasks = tasks.filter(task => !task.completed);

  return (
    <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
      <button
        onClick={() => setOpen(prev => !prev)}
        className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
      >
        <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
          <CheckSquare className="w-6 h-6 text-[#236caf]" />
          Action
        </div>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-[#236caf] transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      {open && (
        <div className="p-4 sm:p-6">
          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

          <div className="space-y-4">
            {lastRungs.length > 0 && (
              <div className="mb-4 space-y-3">
                {lastRungs.map(({ rung, ladderId, goalTitle }) => (
                  <div key={rung.id} className="p-4 bg-blue-100 rounded-lg border-2 border-blue-300">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        <Checkbox
                          id={`rung-${rung.id}`}
                          checked={rung.is_completed}
                          onChange={() => handleToggleRung(rung.id, ladderId, rung.is_completed)}
                          disabled={updatingRung === rung.id}
                        />
                      </div>
                      <label
                        htmlFor={`rung-${rung.id}`}
                        className="flex-1 cursor-pointer"
                      >
                        <p className={`text-gray-800 font-medium text-sm ${rung.is_completed ? 'line-through text-gray-500' : ''}`}>
                          {rung.description}
                        </p>
                        <div className="text-xs font-semibold text-gray-500 mt-2">{(goalTitle || 'Goal').toUpperCase()}</div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {incompleteTasks.length > 0 ? (
              <ul className="space-y-2">
                {incompleteTasks.slice(0, 3).map(task => (
                  <li key={task.id} className="flex items-center gap-3 bg-blue-50 rounded-md px-4 py-2">
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => handleToggleTask(task.id, false)}
                      className="form-checkbox h-5 w-5 text-[#236caf] rounded border-gray-300"
                    />
                    <span className="text-gray-700 text-sm">{task.text}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm">No open tasks yet. You're all caught up!</p>
            )}

            {/* 👇 Add Task Input Section */}
            <div className="flex gap-2 mt-4">
              <input
                type="text"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="Add a new task..."
                className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#236caf]"
              />
              <Button onClick={handleAddTask}>Add</Button>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <Button variant="outline" onClick={() => navigate('/tasks')} className="w-full sm:w-1/2">
                View All
              </Button>
              <GoogleTasksSync className="w-full sm:w-1/2" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}